import { Capacitor, PermissionState } from "@capacitor/core";

import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { useEffect, useState } from "react";
import { storageKey } from "../configs/config";
import { safeLog } from "../error-logger";
import api from "../services/api";
import { useUserStore } from "../store/useUserStore";



export const usePushNotifications = () => {

  const [permission, setPermissionState] = useState<PermissionState>(
    !Capacitor.isNativePlatform()
      ? "denied"
      : (localStorage.getItem(storageKey.pushNotifPerm) as PermissionState) ?? "prompt"
  );

  const setPermission = (value: PermissionState) => {
    setPermissionState(value);
    localStorage.setItem(storageKey.pushNotifPerm, value);
  };

  const [loading, setLoading] = useState(false);

  const getPushPermission = async () => {
    setLoading(true);
    if (Capacitor.isNativePlatform()) {
      try {
        let permStatus = await FirebaseMessaging.checkPermissions();

        if (permStatus.receive === "prompt") {
          permStatus = await FirebaseMessaging.requestPermissions();
        }

        if (permStatus.receive !== "granted") {
          setPermission("denied");
          alert("Push notification access is denied. Please enable it in your settings to continue.");
          setLoading(false);
          return false;
        }

        // Get the token from Firebase
        const { token } = await FirebaseMessaging.getToken();
        console.log("Firebase registration success, token: " + token);
        localStorage.setItem(storageKey.deviceToken, token);
        try {
          await api.updateDeviceToken({ deviceToken: token });
          const { profile, setNotifFlags } = useUserStore.getState();
          if (profile?.notificationFlags) {
            // Explicitly update all flags to enable push on backend
            await api.updateNotificationFlags({
              ...profile.notificationFlags,
              push: true,
            });
            // Update local store
            setNotifFlags({ ...profile.notificationFlags, push: true });
          }
        } catch (err) {
          console.error("Error sending token/flags to backend", err);
        }
        setPermission("granted");
        return true;
      } catch (err) {
        console.error("Capacitor Firebase Messaging Error:", err);
        if (err instanceof Error) {
          safeLog({ type: "notifications", message: err.message });
          if (err.message?.includes("permission")) {
            setPermission("denied");
            alert("Push notification access is denied.");
          }
        }
        return false;
      } finally {
        setLoading(false);
      }
    } else {
      console.log("Push notifications not supported on web in this implementation.");
      setPermission("denied");
      setLoading(false);
      return false;
    }
  };

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const syncPermission = async () => {
      const permStatus = await FirebaseMessaging.checkPermissions();
      console.log("Syncing push permission", permStatus);
      setPermission(permStatus.receive);
    };

    syncPermission();
  }, []);

  return { permission, loading, getPushPermission };
};

